




































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Account } from '@/Account'
import { Feedback, roleType, filterTypes, sortOrderTypes } from '@/Feedback'
import { TokenUser } from '@/TokenUser'
import * as utils from '@/utils';
import FeedbackListComponent from '@/components/FeedbackListComponent.vue';
import { IPaginationResult } from '@/PaginationResult'
import GenericDropdown from '@/components/GenericDropdown.vue';
import * as utils_auth from '@/utils_auth';
import { globalUC } from '../UserCache';

@Component({
  name: 'FeedbackComponent',
  components: { FeedbackListComponent, GenericDropdown }
})
export default class FeedbackComponent extends Vue {
  // prop reactive member variables publically available to the html template

  // public reactive member variables available to the html template
  public lastFetchedURL: string = null;
  public flowAddress: string = null;
  public feedbacks: Feedback[] = null
  public paginationResult: IPaginationResult = null
  public account: Account = null

  // UI and filter options
  public sortOrders: Array<{ label: string, sort: sortOrderTypes }> = [
    { label: 'Sort recent first', sort: 'Date_DESC' },
    { label: 'Sort oldest first', sort: 'Date_ASC' },
    { label: 'Highest ratings first', sort: 'Rating_DESC' },
    { label: 'Lowest ratings first', sort: 'Rating_ASC' },
  ]
  public sortOrder: { label: string, sort: string } = this.sortOrders[0]
  public filter: filterTypes = filterTypes.AllReceived
  public filterOptions: Array<{ text: string, value: filterTypes, disabled: boolean }> = [
    { text: 'All Received', value: filterTypes.AllReceived, disabled: false },
    { text: 'Received as Seller', value: filterTypes.ReceivedAsSeller, disabled: false },
    { text: 'Received as Buyer', value: filterTypes.ReceivedAsBuyer, disabled: false },
    { text: 'Left for Others', value: filterTypes.LeftForOthers, disabled: false },
  ]

  // public functions available to the html template
  public get loading(): boolean { return this.$store.state.loading }
  public get user(): TokenUser { return this.$store.getters.userObject }
  public get userId(): string { return this.$store.getters.userId }

  public async onChangedSort(sortOrder: { label: string, sort: string }) {
    this.sortOrder = sortOrder
    this.pageNumberToLoadNext = 1
    await this.fetchData()
  }

  public async infiniteHandler(state: any): Promise<void> {
    const numReturned: number = await this.fetchData(true)
    if (numReturned === 0) {
      if (this.$store.state.devmode) console.log(`infiniteHandler(): state.complete after getting 0 items`)
      state.complete()
    } else {
      state.loaded();
    }
  }

  public async setFlowAddress(flowAddress: string, forceRefetch: boolean) {
    if (this.$store.state.devmode) console.log(`FeedbackComponent.setFlowAddress(${flowAddress}, ${forceRefetch}) while this.flowAddress===${this.flowAddress}`)
    if (forceRefetch || flowAddress !== this.flowAddress) {
      this.flowAddress = flowAddress
      this.pageNumberToLoadNext = 1
      this.feedbacks = null
      this.paginationResult = null
      await this.fetchData()
    }
  }

  @Watch('filter')
  public async onChangedFilter() {
    this.pageNumberToLoadNext = 1
    this.feedbacks = []
    this.paginationResult = null
    await this.fetchData()
  }

  // private, non-reactive member variables
  private lastFetchSucceeded: boolean;
  private pageNumberToLoadNext: number = 1

  // private functions not available directly to HTML template
  private async mounted() {
    if (this.$store.state.devmode) console.log(`${this.$options.name} mounted()`);
  }
  private async beforeDestroy() { 
    if (this.$store.state.devmode) console.log(`${this.$options.name} beforeDestroy()`)
  }

  private async fetchData(calledByInfiniteHandlerOrTimer?: boolean): Promise<number> {
    const header: Feedback = new Feedback(null)
    header.auctionId = null
    header.created = new Date()

    if (!this.flowAddress) return 0
    let url: string = utils.getURLprefix() + `/api/topshot/feedback/${this.filter === filterTypes.LeftForOthers ? 'author' : 'ratee'}/`;
    url += this.flowAddress
    url += `?page=${this.pageNumberToLoadNext}`;
    url += `&sortOrder=${this.sortOrder.sort}`;
    url += `&filter=${this.filter}`;

    // don't fetch if we're in the middle of fetching same url
    if (this.loading) {
      if (this.lastFetchedURL === url || calledByInfiniteHandlerOrTimer) {
        if (this.$store.state.devmode) console.log(`FeedbackComponent.fetchData() aborting calledByInfiniteHandler and we're already loading`)
        await utils.sleep(1000)     // wait 1 s so we don't go into 100% CPU infinite loop
        return
      }
    }

    this.$store.commit('setLoading', { loading: true });
    this.lastFetchedURL = url;
    try {
      // console.log(url)
      this.lastFetchSucceeded = false;
      const response: any = await utils.makeGetCall(url);

      // whoa, we got another call after this one, so let's abort this one cuz the latest one should take precedent
      if (this.lastFetchedURL !== url) return

      // in case old version
      if (utils.isClientOld()) {
        if (this.$store.state.devmode) console.log("Calling this.$router.go(0)");
        this.$router.go(0);
        this.$store.commit('setLoading', { loading: false });
        return;
      }

      // error check
      if (response.data.error) {
        utils.toast(this, 'Error', response.data.error)
        this.$store.commit('setLoading', { loading: false });
        return
      }

      // pagination result
      this.paginationResult = response.data.paginationResult

      // load up the account
      if (response.data.account) {
        this.account = new Account(response.data.account)
      }

      // load up the feedbacks
      let newRawItems: Feedback[] = response.data.feedbacks
      if (newRawItems) {
        newRawItems = newRawItems.map((po) => new Feedback(po));
        if (this.pageNumberToLoadNext === 1) this.feedbacks = [ header ].concat(newRawItems);
        else this.feedbacks = this.feedbacks.concat(newRawItems)

        this.pageNumberToLoadNext++
        this.lastFetchSucceeded = true

        if (this.$store.state.devmode) utils.toast(this, 'DEBUGGING', `Loaded ${this.feedbacks.length} feedbacks`)

        return newRawItems.length
      } else {
        this.lastFetchSucceeded = false;
        return 0
      }
    } catch (error) {
      if (error.toString().includes('Network Error') || error.toString().includes('status code 502')) utils.toast(this, 'Error', 'Undergoing Maintenance'); else utils.toast(this, 'Error', 'Could not load url')
      if (this.$store.state.devmode) console.log(error);
      return 0
    } finally {
      this.$store.commit("setLoading", { loading: false });
    }

  }
}
